import { useState } from 'react';
import { AlertTriangle, Plus, Pencil, Trash2 } from 'lucide-react';
import BulletCreditLine from './charts/BulletCreditLine';
import type { CreditLineEntry, CreateCreditLineData } from '../../types/cashCockpit';
import { GROUP_COMPANIES, SUPPORTED_CURRENCIES } from '../../types/cashCockpit';
import { fetchApi } from '../../services/api';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useToast } from '../ui/Toast';

interface Props {
    data: CreditLineEntry[];
    canEdit: boolean;
    onReload: () => void;
}

const SOURCE_BADGE: Record<CreditLineEntry['source'], string> = {
    BankAccount: 'bg-blue-50 text-blue-700 border-blue-200',
    Manual: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function CreditLinesTab({ data, canEdit, onReload }: Props) {
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<CreditLineEntry | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
    const { toast } = useToast();

    const grouped = data.reduce((acc, e) => {
        if (!acc[e.companyCode]) acc[e.companyCode] = [];
        acc[e.companyCode].push(e);
        return acc;
    }, {} as Record<string, CreditLineEntry[]>);

    const doDelete = async () => {
        if (confirmDeleteId == null) return;
        try {
            await fetchApi(`/cash-cockpit/credit-lines/${confirmDeleteId}`, { method: 'DELETE' });
            toast('Credit line deleted', 'success');
            onReload();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Delete failed', 'error');
        } finally {
            setConfirmDeleteId(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                    Sources: bank-account-linked facilities (Bank Balance → Bank Accounts with AccountType=CreditLine)
                    + manual standalone facilities below.
                </div>
                {canEdit && (
                    <button
                        onClick={() => { setEditing(null); setShowForm(true); }}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        <Plus className="w-3 h-3" /> Add Credit Line
                    </button>
                )}
            </div>

            {data.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-500 text-sm">
                    No credit lines configured. Classify bank accounts as CreditLine or FixedTermLoan in Bank Balance, or add a manual credit line above.
                </div>
            ) : (
                Object.entries(grouped).sort().map(([company, lines]) => (
                    <div key={company} className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-semibold text-gray-900 uppercase">{company}</h3>
                            <span className="text-xs text-gray-500">{lines.length} line{lines.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-1 divide-y divide-gray-100">
                            {lines.map(l => (
                                <div key={`${l.source}-${l.bankID}`} className="pt-2 first:pt-0">
                                    <div className="flex items-start gap-2">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded border mt-1 ${SOURCE_BADGE[l.source]}`}>
                                            {l.source}
                                        </span>
                                        <div className="flex-1">
                                            <BulletCreditLine
                                                label={l.bankAccountName}
                                                sublabel={l.bankCompanyName}
                                                utilised={l.utilisedFCY}
                                                limit={l.creditLimit}
                                                currencyCode={l.currencyCode}
                                            />
                                        </div>
                                        {canEdit && l.source === 'Manual' && l.id != null && (
                                            <div className="flex items-center gap-1 mt-1">
                                                <button onClick={() => { setEditing(l); setShowForm(true); }}
                                                    className="text-blue-600 hover:text-blue-800"><Pencil className="w-3 h-3" /></button>
                                                <button onClick={() => setConfirmDeleteId(l.id!)}
                                                    className="text-red-600 hover:text-red-800"><Trash2 className="w-3 h-3" /></button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-1 pl-48">
                                        {l.interestRate != null && (
                                            <span>IR: {(l.interestRate * 100).toFixed(2)}%</span>
                                        )}
                                        {l.maturityDate && (
                                            <span className={`flex items-center gap-1 ${
                                                l.maturityAlert === 'Critical' ? 'text-[#D7191C] font-semibold'
                                                : l.maturityAlert === 'Warning' ? 'text-[#F28C28]'
                                                : ''
                                            }`}>
                                                {l.maturityAlert !== 'None' && <AlertTriangle className="w-3 h-3" />}
                                                Matures: {l.maturityDate}
                                                {l.daysToMaturity != null && <span className="text-gray-400">({l.daysToMaturity}d)</span>}
                                            </span>
                                        )}
                                        {l.renewalType && l.renewalType !== 'None' && (
                                            <span>Renewal: {l.renewalType}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            )}

            {showForm && (
                <CreditLineModal
                    entry={editing}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSaved={() => { setShowForm(false); setEditing(null); onReload(); }}
                />
            )}

            {confirmDeleteId != null && (
                <ConfirmModal
                    title="Delete credit line?"
                    message="This credit line will be deactivated and no longer appear in the Cash Cockpit."
                    confirmLabel="Delete"
                    variant="danger"
                    onConfirm={doDelete}
                    onCancel={() => setConfirmDeleteId(null)}
                />
            )}
        </div>
    );
}

interface ModalProps {
    entry: CreditLineEntry | null;
    onClose: () => void;
    onSaved: () => void;
}

function CreditLineModal({ entry, onClose, onSaved }: ModalProps) {
    const isEdit = entry?.source === 'Manual' && entry.id != null;
    const [form, setForm] = useState({
        companyCode: entry?.companyCode ?? 'bin',
        facilityName: entry?.bankAccountName ?? '',
        counterpartyName: entry?.bankCompanyName ?? '',
        currencyCode: entry?.currencyCode ?? 'EUR',
        creditLimit: entry?.creditLimit?.toString() ?? '',
        utilisedAmount: entry?.utilisedFCY?.toString() ?? '0',
        interestRate: entry?.interestRate != null ? (entry.interestRate * 100).toString() : '',
        maturityDate: entry?.maturityDate ?? '',
        renewalType: (entry?.renewalType as 'Evergreen' | 'FixedTerm' | 'OnDemand' | null) ?? null,
        notes: entry?.notes ?? '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const limitNum = parseFloat(form.creditLimit);
    const utilisedNum = parseFloat(form.utilisedAmount);
    const interestNum = form.interestRate ? parseFloat(form.interestRate) / 100 : null;
    const canSave =
        form.companyCode && form.facilityName.trim() && form.counterpartyName.trim() &&
        form.currencyCode && !Number.isNaN(limitNum) && limitNum > 0 && !saving;

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            const payload: CreateCreditLineData = {
                companyCode: form.companyCode,
                facilityName: form.facilityName.trim(),
                counterpartyName: form.counterpartyName.trim(),
                currencyCode: form.currencyCode,
                creditLimit: limitNum,
                utilisedAmount: !Number.isNaN(utilisedNum) ? utilisedNum : 0,
                interestRate: interestNum,
                maturityDate: form.maturityDate || null,
                renewalType: form.renewalType,
                notes: form.notes.trim() || null,
            };

            if (isEdit) {
                await fetchApi(`/cash-cockpit/credit-lines/${entry!.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload),
                });
            } else {
                await fetchApi('/cash-cockpit/credit-lines', {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
            }
            onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-xl">
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="text-base font-semibold">{isEdit ? 'Edit Credit Line' : 'Add Credit Line'}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-lg">×</button>
                </div>
                <div className="p-5 space-y-3 text-sm">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-xs">{error}</div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs text-gray-600">Entity *</span>
                            <select value={form.companyCode}
                                onChange={e => setForm({ ...form, companyCode: e.target.value })}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 uppercase">
                                {GROUP_COMPANIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs text-gray-600">Currency *</span>
                            <select value={form.currencyCode}
                                onChange={e => setForm({ ...form, currencyCode: e.target.value })}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5">
                                {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-xs text-gray-600">Facility name *</span>
                        <input type="text" value={form.facilityName}
                            onChange={e => setForm({ ...form, facilityName: e.target.value })}
                            placeholder="e.g. BBVA Revolving Facility"
                            className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5" />
                    </label>
                    <label className="block">
                        <span className="text-xs text-gray-600">Counterparty / lender *</span>
                        <input type="text" value={form.counterpartyName}
                            onChange={e => setForm({ ...form, counterpartyName: e.target.value })}
                            placeholder="e.g. BBVA"
                            className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs text-gray-600">Credit limit (FCY) *</span>
                            <input type="number" step="0.01" value={form.creditLimit}
                                onChange={e => setForm({ ...form, creditLimit: e.target.value })}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 font-mono" />
                        </label>
                        <label className="block">
                            <span className="text-xs text-gray-600">Utilised (FCY)</span>
                            <input type="number" step="0.01" value={form.utilisedAmount}
                                onChange={e => setForm({ ...form, utilisedAmount: e.target.value })}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 font-mono" />
                        </label>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <label className="block">
                            <span className="text-xs text-gray-600">Interest (% p.a.)</span>
                            <input type="number" step="0.01" value={form.interestRate}
                                onChange={e => setForm({ ...form, interestRate: e.target.value })}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 font-mono" />
                        </label>
                        <label className="block">
                            <span className="text-xs text-gray-600">Maturity</span>
                            <input type="date" value={form.maturityDate}
                                onChange={e => setForm({ ...form, maturityDate: e.target.value })}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5" />
                        </label>
                        <label className="block">
                            <span className="text-xs text-gray-600">Renewal</span>
                            <select value={form.renewalType ?? ''}
                                onChange={e => setForm({ ...form, renewalType: (e.target.value || null) as typeof form.renewalType })}
                                className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5">
                                <option value="">—</option>
                                <option value="Evergreen">Evergreen</option>
                                <option value="FixedTerm">Fixed Term</option>
                                <option value="OnDemand">On Demand</option>
                            </select>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-xs text-gray-600">Notes</span>
                        <textarea value={form.notes}
                            onChange={e => setForm({ ...form, notes: e.target.value })}
                            rows={2}
                            className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5" />
                    </label>
                </div>
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
                    <button onClick={onClose} disabled={saving}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                    <button onClick={handleSave} disabled={!canSave}
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
}
